import { Link } from "@/lib/react-router-dom-shim";
import { useTranslation } from "react-i18next";
import { FooterPopularHotels } from "./FooterPopularHotels";
import { FooterPopularDestinations } from "./FooterPopularDestinations";
import { FooterPhotoCredits } from "./FooterPhotoCredits";
import { Instagram, Facebook } from "lucide-react";

// Custom brand icons as SVG components
const GoogleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const TrustpilotIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 1l3.09 6.26L22 8.27l-5 4.87 1.18 6.88L12 16.77l-6.18 3.25L7 13.14 2 8.27l6.91-1.01L12 1z" />
  </svg>
);

const ReclameAquiIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    {/* Simplified RA letters optimized for small size */}
    <path d="M2 4h6c2.5 0 4.5 1.8 4.5 4.5S10.5 13 8 13H6.5l5 7H8l-4.5-6.5H3V20H1V4h1zm2 6h3.5c1.2 0 2.2-.8 2.2-2.2S8.7 5.6 7.5 5.6H4V10z" />
    <path d="M13 20l5.5-16h2l5.5 16h-2.5l-1.2-4h-5.6l-1.2 4H13zm4.8-6h3.4l-1.7-5.5L17.8 14z" />
  </svg>
);

const socialLinks = [
  { href: "https://www.instagram.com/foradarota.com.br/", icon: Instagram, label: "Instagram" },
  { href: "https://www.facebook.com/people/Fora-da-Rota/61586050490567/", icon: Facebook, label: "Facebook" },
  { href: "https://share.google/H62SZeHrmPRCLZ5Ei", icon: GoogleIcon, label: "Google Reviews" },
  { href: "https://br.trustpilot.com/review/foradarota.com", icon: TrustpilotIcon, label: "TrustPilot" },
  { href: "https://www.reclameaqui.com.br/empresa/gj-empreendimentos-ltda/", icon: ReclameAquiIcon, label: "Reclame Aqui" },
];

export const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="relative bg-gradient-to-br from-primary/95 via-primary to-primary/90 py-12 text-primary-foreground overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
      </div>
      
      <div className="container relative mx-auto px-4">
        {/* Top section - 4 columns: Brand+Social | Hotels | Support | Company */}
        <div className="grid gap-8 md:grid-cols-4 mb-8">
          {/* Brand column */}
          <div>
            <h3 className="font-bold text-xl text-primary-foreground">Fora da Rota</h3>
            <p className="text-sm text-primary-foreground/80 mt-1">
              {t('hero.subtitle')}
            </p>
            {/* Social icons - discrete, below subtitle */}
            <div className="flex items-center gap-3 mt-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/50 hover:text-primary-foreground transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Hotels column */}
          <div>
            <h4 className="mb-3 font-semibold text-sm text-primary-foreground">{t('nav.hotels')}</h4>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/hoteles" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('nav.hotels')}</Link></li>
              <li><Link to="/resorts" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('nav.resorts')}</Link></li>
              <li><Link to="/directorio-hoteles" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('hotelDirectory.title')}</Link></li>
            </ul>
          </div>

          {/* Support column */}
          <div>
            <h4 className="mb-3 font-semibold text-sm text-primary-foreground">{t('footer.support')}</h4>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/faq" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('footer.faq')}</Link></li>
              <li><Link to="/profile" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('nav.myProfile')}</Link></li>
              <li><Link to="/my-reservations" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('nav.myReservations')}</Link></li>
            </ul>
          </div>

          {/* Company column */}
          <div>
            <h4 className="mb-3 font-semibold text-sm text-primary-foreground">{t('footer.company')}</h4>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/quienes-somos" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('footer.aboutUs')}</Link></li>
              <li><Link to="/terminos-y-condiciones" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('footer.terms')}</Link></li>
              <li><Link to="/politica-de-privacidad" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('footer.privacy')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Popular Destinations Section */}
        <FooterPopularDestinations limit={6} />

        {/* Popular Hotels Section - 3 columns, 9 initial, 27 max */}
        <FooterPopularHotels initialLimit={9} expandedLimit={27} />

        {/* Bottom section with copyright and photo credits */}
        <div className="mt-8 pt-6 border-t border-primary-foreground/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-primary-foreground/60">
            © 2024 Fora da Rota. {t('footer.rights')}
          </p>
          {/* Photo Credits as expandable link */}
          <FooterPhotoCredits />
        </div>
      </div>
    </footer>
  );
};
